import React, { useEffect, useState } from 'react';
import { Table, Button, Tag, Alert, Card, Input, Select, Space, Modal, Form, Switch, Avatar, Typography, Row, Col, Statistic, Progress, Rate, Dropdown, message, Popconfirm } from 'antd';
import ModernImageUpload from '../../UI/ModernImageUpload';
import { ShopOutlined, SearchOutlined, EditOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, StarOutlined, ClockCircleOutlined, DownOutlined, SettingOutlined, DeleteOutlined, BlockOutlined } from '@ant-design/icons';
import api from '../../../services/api';
import { getStatusColor } from '../../../utils/ui';
import { useAuth } from '../../../contexts/AuthContext';

function AdminVendors() {
    const { user } = useAuth();
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [cityFilter, setCityFilter] = useState('');
    const [cities, setCities] = useState([]);
    const [viewingVendor, setViewingVendor] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [editVisible, setEditVisible] = useState(false);
    const [editForm] = Form.useForm();
    const [createVisible, setCreateVisible] = useState(false);
    const [createForm] = Form.useForm();
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [bulkActionLoading, setBulkActionLoading] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [citiesRes, vendorsRes] = await Promise.all([
                api.getCities(),
                api.adminListVendors({ per_page: 100 })
            ]);
            setCities(citiesRes.data);
            setVendors(vendorsRes.data.data || []);
        } catch (e) {
            setError('Failed to load vendor data');
        } finally {
            setLoading(false);
        }
    };

    const verifyVendor = async (vendorId) => {
        try {
            await api.adminVerifyVendor(vendorId);
            loadData();
        } catch (e) {
            setError('Failed to verify vendor');
        }
    };

    const rejectVendor = async (vendorId) => {
        try {
            // Add reject vendor API call
            console.log('Rejecting vendor:', vendorId);
            loadData();
        } catch (e) {
            setError('Failed to reject vendor');
        }
    };

    

    const exportCSV = () => {
        const rows = [
            ['id','business_name','email','phone','city','min_order_amount','is_verified','is_active']
        ];
        filteredVendors.forEach(v => rows.push([
            v.id, v.business_name, v.email, v.phone, v.city?.name || '', v.min_order_amount || '', v.is_verified ? 1 : 0, v.is_active ? 1 : 0
        ]));
        const csv = rows.map(r => r.map((f) => `"${String(f ?? '').replaceAll('"','""')}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'vendors.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const importCSV = async (file) => {
        try {
            const text = await file.text();
            const lines = text.split(/\r?\n/).filter(Boolean);
            if (lines.length <= 1) { message.warning('Empty CSV'); return; }
            const header = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g,''));
            const idx = (k) => header.indexOf(k);
            const nameIdx = idx('business_name');
            const emailIdx = idx('email');
            const phoneIdx = idx('phone');
            const cityIdx = idx('city');
            const minIdx = idx('min_order_amount');
            let created = 0;
            for (let i=1;i<lines.length;i++) {
                const cols = lines[i].split(',').map(c => c.trim().replace(/^"|"$/g,''));
                if (!cols[nameIdx]) continue;
                const payload = {
                    business_name: cols[nameIdx],
                    email: cols[emailIdx] || undefined,
                    phone: cols[phoneIdx] || undefined,
                    // In absence of city id lookup by name, keep it undefined or map client-side
                    min_order_amount: cols[minIdx] ? Number(cols[minIdx]) : undefined,
                };
                try { await api.adminCreateVendor(payload); created++; } catch {}
            }
            message.success(`Imported ${created} vendors`);
            loadData();
        } catch (e) {
            message.error('Failed to import CSV');
        }
    };

    const viewVendorDetails = (vendor) => {
        setViewingVendor(vendor);
        setModalVisible(true);
    };

    const openEdit = (vendor) => {
        setViewingVendor(vendor);
        editForm.setFieldsValue({
            business_name: vendor.business_name,
            email: vendor.email,
            phone: vendor.phone,
            city_id: vendor.city_id,
            min_order_amount: vendor.min_order_amount,
            is_active: !!vendor.is_active,
            description: vendor.description,
            address: vendor.address,
            gallery: (vendor.gallery || []).map(g => ({ id: g.id, url: g.url, alt_text: g.alt_text, name: g.original_name })),
        });
        setEditVisible(true);
    };

    const saveVendor = async () => {
        try {
            const values = await editForm.validateFields();
            const payload = {
                business_name: values.business_name,
                email: values.email,
                phone: values.phone,
                city_id: values.city_id,
                min_order_amount: values.min_order_amount,
                is_active: values.is_active ? 1 : 0,
                description: values.description,
                address: values.address,
                gallery_ids: (values.gallery || []).map(i => i.id).filter(Boolean),
            };
            await api.adminUpdateVendor(viewingVendor.id, payload);
            message.success('Vendor updated');
            setEditVisible(false);
            loadData();
        } catch (e) {
            if (e?.errorFields) return; // form validation
            message.error(e.message || 'Failed to update vendor');
        }
    };

    const createVendor = async () => {
        try {
            const values = await createForm.validateFields();
            const payload = {
                business_name: values.business_name,
                email: values.email,
                phone: values.phone,
                city_id: values.city_id,
                min_order_amount: values.min_order_amount || 0,
                is_active: values.is_active ? 1 : 0,
                description: values.description || '',
                address: values.address || '',
            };
            await api.adminCreateVendor(payload);
            message.success('Vendor created successfully');
            setCreateVisible(false);
            createForm.resetFields();
            loadData();
        } catch (e) {
            if (e?.errorFields) return; // form validation
            message.error(e.message || 'Failed to create vendor');
        }
    };

    const deleteVendor = async (vendor) => {
        try {
            await api.adminDeleteVendor(vendor.id);
            message.success('Vendor deleted');
            loadData();
        } catch (e) {
            message.error(e.message || 'Failed to delete vendor');
        }
    };

    const bulkVerify = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Please select vendors to verify');
            return;
        }
        setBulkActionLoading(true);
        try {
            await Promise.all(selectedRowKeys.map(id => api.adminVerifyVendor(id)));
            message.success(`${selectedRowKeys.length} vendors verified`);
            setSelectedRowKeys([]);
            loadData();
        } catch (e) {
            message.error(e.message || 'Failed to verify vendors');
        } finally {
            setBulkActionLoading(false);
        }
    };

    const bulkReject = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Please select vendors to reject');
            return;
        }
        setBulkActionLoading(true);
        try {
            await Promise.all(selectedRowKeys.map(id => api.adminRejectVendor(id)));
            message.success(`${selectedRowKeys.length} vendors rejected`);
            setSelectedRowKeys([]);
            loadData();
        } catch (e) {
            message.error(e.message || 'Failed to reject vendors');
        } finally {
            setBulkActionLoading(false);
        }
    };

    const filteredVendors = vendors.filter(vendor => {
        const matchesSearch = vendor.business_name?.toLowerCase().includes(searchText.toLowerCase()) ||
                            vendor.email?.toLowerCase().includes(searchText.toLowerCase());
        const matchesStatus = !statusFilter || 
            (statusFilter === 'verified' && vendor.is_verified) ||
            (statusFilter === 'pending' && !vendor.is_verified) ||
            (statusFilter === 'active' && vendor.is_active);
        const matchesCity = !cityFilter || vendor.city_id === cityFilter;
        return matchesSearch && matchesStatus && matchesCity;
    });

    const columns = [
        {
            title: 'Vendor',
            key: 'vendor',
            render: (_, vendor) => (
                <div className="flex items-center space-x-3">
                    <Avatar icon={<ShopOutlined />} />
                    <div>
                        <div className="font-medium">{vendor.business_name}</div>
                        <div className="text-sm text-gray-500">{vendor.email}</div>
                    </div>
                </div>
            )
        },
        {
            title: 'City',
            dataIndex: ['city', 'name'],
            key: 'city'
        },
        {
            title: 'Rating',
            key: 'rating',
            render: (_, vendor) => (
                <div className="flex items-center space-x-1">
                    <Rate disabled value={vendor.rating || 0} size="small" />
                    <span className="text-sm text-gray-500">({vendor.total_reviews || 0})</span>
                </div>
            )
        },
        {
            title: 'Min Order',
            dataIndex: 'min_order_amount',
            key: 'min_order',
            render: (amount) => amount ? `₹${amount}` : '-'
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, vendor) => (
                <Space direction="vertical" size="small">
                    <Tag color={getStatusColor(vendor.is_verified ? 'confirmed' : 'pending')}>
                        {vendor.is_verified ? 'Verified' : 'Pending'}
                    </Tag>
                    <Tag color={getStatusColor(vendor.is_active ? 'success' : 'cancelled')}>
                        {vendor.is_active ? 'Active' : 'Inactive'}
                    </Tag>
                </Space>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, vendor) => (
                <Space>
                    <Button 
                        size="small" 
                        icon={<EyeOutlined />}
                        onClick={() => viewVendorDetails(vendor)}
                    >
                        View
                    </Button>
                    <Button 
                        size="small" 
                        icon={<EditOutlined />}
                        onClick={() => openEdit(vendor)}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Delete Vendor"
                        description="Are you sure you want to delete this vendor? This action cannot be undone."
                        onConfirm={() => deleteVendor(vendor)}
                        okText="Delete"
                        cancelText="Cancel"
                        okType="danger"
                    >
                        <Button danger size="small" icon={<DeleteOutlined />}>Delete</Button>
                    </Popconfirm>
                    {!vendor.is_verified && (
                        <>
                            <Button 
                                type="primary" 
                                size="small" 
                                icon={<CheckCircleOutlined />}
                                onClick={() => verifyVendor(vendor.id)}
                            >
                                Verify
                            </Button>
                            <Button 
                                danger 
                                size="small" 
                                icon={<CloseCircleOutlined />}
                                onClick={() => rejectVendor(vendor.id)}
                            >
                                Reject
                            </Button>
                        </>
                    )}
                    <Popconfirm title="Delete vendor?" onConfirm={() => deleteVendor(vendor.id)}>
                        <Button danger size="small" icon={<DeleteOutlined />}>Delete</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const stats = {
        totalVendors: vendors.length,
        verifiedVendors: vendors.filter(v => v.is_verified).length,
        pendingVendors: vendors.filter(v => !v.is_verified).length,
        activeVendors: vendors.filter(v => v.is_active).length
    };

    if (user && user.user_type !== 'admin') {
        return (
            <div className="p-6">
                <Alert type="error" message="Access denied" description="You do not have permission to view this page." />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <Typography.Title level={2} className="!mb-0">Vendor Management</Typography.Title>
                            <Typography.Text type="secondary">Manage vendor accounts and verifications</Typography.Text>
                        </div>
                        <div>
                            <Space>
                                <input type="file" accept=".csv" onChange={(e) => e.target.files?.[0] && importCSV(e.target.files[0])} />
                                <Button onClick={exportCSV}>Export CSV</Button>
                                <Button disabled={!selectedRowKeys.length} loading={bulkActionLoading} onClick={bulkVerify}>Bulk Verify</Button>
                                <Button disabled={!selectedRowKeys.length} loading={bulkActionLoading} onClick={bulkReject}>Bulk Reject</Button>
                                <Button type="primary" onClick={() => setCreateVisible(true)} icon={<EditOutlined />}>Add Vendor</Button>
                            </Space>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto p-6">
                {error && <Alert type="error" message={error} className="mb-6" />}

                {/* Stats */}
                <Row gutter={[16, 16]} className="mb-6">
                    <Col xs={24} sm={6}>
                        <Card className="card-compact">
                            <Statistic
                                title="Total Vendors"
                                value={stats.totalVendors}
                                prefix={<ShopOutlined />}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={6}>
                        <Card className="card-compact">
                            <Statistic
                                title="Verified"
                                value={stats.verifiedVendors}
                                prefix={<CheckCircleOutlined />}
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={6}>
                        <Card className="card-compact">
                            <Statistic
                                title="Pending"
                                value={stats.pendingVendors}
                                prefix={<ClockCircleOutlined />}
                                valueStyle={{ color: '#fa8c16' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={6}>
                        <Card className="card-compact">
                            <Statistic
                                title="Active"
                                value={stats.activeVendors}
                                prefix={<StarOutlined />}
                                valueStyle={{ color: '#722ed1' }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Filters and Search */}
                <Card className="mb-6">
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} sm={8}>
                            <Input
                                placeholder="Search vendors..."
                                prefix={<SearchOutlined />}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                allowClear
                            />
                        </Col>
                        <Col xs={24} sm={5}>
                            <Select
                                placeholder="Status"
                                value={statusFilter}
                                onChange={setStatusFilter}
                                allowClear
                                style={{ width: '100%' }}
                            >
                                <Select.Option value="verified">Verified</Select.Option>
                                <Select.Option value="pending">Pending</Select.Option>
                                <Select.Option value="active">Active</Select.Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={5}>
                            <Select
                                placeholder="City"
                                value={cityFilter}
                                onChange={setCityFilter}
                                allowClear
                                style={{ width: '100%' }}
                            >
                                {cities.map(city => (
                                    <Select.Option key={city.id} value={city.id}>{city.name}</Select.Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} sm={6}>
                            <Space>
                                <Button onClick={() => { setSearchText(''); setStatusFilter(''); setCityFilter(''); }}>
                                    Clear
                                </Button>
                            </Space>
                        </Col>
                    </Row>

                    {/* Bulk Actions */}
                    {selectedRowKeys.length > 0 && (
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex items-center justify-between">
                                <span className="text-blue-800 font-medium">
                                    {selectedRowKeys.length} vendor(s) selected
                                </span>
                                <Space>
                                    <Button 
                                        type="primary" 
                                        icon={<CheckCircleOutlined />}
                                        onClick={bulkVerify}
                                        loading={bulkActionLoading}
                                    >
                                        Bulk Verify
                                    </Button>
                                    <Button 
                                        danger 
                                        icon={<CloseCircleOutlined />}
                                        onClick={bulkReject}
                                        loading={bulkActionLoading}
                                    >
                                        Bulk Reject
                                    </Button>
                                    <Button onClick={() => setSelectedRowKeys([])}>
                                        Clear Selection
                                    </Button>
                                </Space>
                            </div>
                        </div>
                    )}
                </Card>

                {/* Vendors Table */}
                <Card>
                    <Table
                        columns={columns}
                        dataSource={filteredVendors}
                        rowKey="id"
                        loading={loading}
                        rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} vendors`
                        }}
                        scroll={{ x: true }}
                    />
                </Card>

                {/* Vendor Details Modal */}
                <Modal
                    title="Vendor Details"
                    open={modalVisible}
                    onCancel={() => setModalVisible(false)}
                    footer={[
                        <Button key="close" onClick={() => setModalVisible(false)}>
                            Close
                        </Button>
                    ]}
                    width={800}
                >
                    {viewingVendor && (
                        <div className="space-y-4">
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <Typography.Text strong>Business Name:</Typography.Text>
                                    <Typography.Paragraph>{viewingVendor.business_name}</Typography.Paragraph>
                                </Col>
                                <Col span={12}>
                                    <Typography.Text strong>Email:</Typography.Text>
                                    <Typography.Paragraph>{viewingVendor.email}</Typography.Paragraph>
                                </Col>
                            </Row>
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <Typography.Text strong>Phone:</Typography.Text>
                                    <Typography.Paragraph>{viewingVendor.phone}</Typography.Paragraph>
                                </Col>
                                <Col span={12}>
                                    <Typography.Text strong>City:</Typography.Text>
                                    <Typography.Paragraph>{viewingVendor.city?.name}</Typography.Paragraph>
                                </Col>
                            </Row>
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <Typography.Text strong>Rating:</Typography.Text>
                                    <div className="flex items-center space-x-2">
                                        <Rate disabled value={viewingVendor.rating || 0} />
                                        <span>({viewingVendor.total_reviews || 0} reviews)</span>
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <Typography.Text strong>Min Order:</Typography.Text>
                                    <Typography.Paragraph>₹{viewingVendor.min_order_amount || 0}</Typography.Paragraph>
                                </Col>
                            </Row>
                            <div>
                                <Typography.Text strong>Description:</Typography.Text>
                                <Typography.Paragraph>{viewingVendor.description || 'No description provided'}</Typography.Paragraph>
                            </div>
                            <div>
                                <Typography.Text strong>Address:</Typography.Text>
                                <Typography.Paragraph>{viewingVendor.address || 'No address provided'}</Typography.Paragraph>
                            </div>
                        </div>
                    )}
                </Modal>

                {/* Edit Vendor Modal */}
                <Modal
                    title="Edit Vendor"
                    open={editVisible}
                    onCancel={() => setEditVisible(false)}
                    onOk={saveVendor}
                    okText="Save"
                    width={900}
                >
                    <Form form={editForm} layout="vertical">
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="business_name" label="Business Name" rules={[{ required: true, message: 'Enter business name' }, { min: 2, message: 'Too short' }]}>
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Enter email' }, { type: 'email', message: 'Invalid email' }]}>
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="phone" label="Phone" rules={[{ pattern: /^\+?[0-9\-\s]{7,15}$/i, message: 'Invalid phone' }]}>
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="city_id" label="City" rules={[{ required: true, message: 'Select city' }]}>
                                    <Select placeholder="Select city">
                                        {cities.map(c => (
                                            <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="min_order_amount" label="Min Order Amount" rules={[{ type: 'number', transform: (v)=> Number(v), min: 0, message: 'Must be >= 0' }]}>
                                    <Input type="number" min={0} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="is_active" valuePropName="checked" label="Active">
                                    <Switch />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item name="address" label="Address" rules={[{ max: 200, message: 'Max 200 chars' }]}>
                            <Input.TextArea rows={2} />
                        </Form.Item>
                        <Form.Item name="description" label="Description" rules={[{ max: 1000, message: 'Max 1000 chars' }]}>
                            <Input.TextArea rows={4} />
                        </Form.Item>

                        <Typography.Title level={5}>Vendor Gallery</Typography.Title>
                        <Form.Item name="gallery" valuePropName="value">
                            <ModernImageUpload
                                currentImage={null}
                                onUpload={async (file) => {
                                    // Handle gallery upload
                                    return { file_path: URL.createObjectURL(file) };
                                }}
                                onRemove={() => {}}
                            />
                        </Form.Item>
                    </Form>
                </Modal>

                {/* Create Vendor Modal */}
                <Modal
                    title="Create New Vendor"
                    open={createVisible}
                    onCancel={() => setCreateVisible(false)}
                    onOk={createVendor}
                    okText="Create"
                    width={900}
                >
                    <Form form={createForm} layout="vertical">
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="business_name" label="Business Name" rules={[{ required: true, message: 'Enter business name' }, { min: 2, message: 'Too short' }]}>
                                    <Input placeholder="Enter business name" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Enter email' }, { type: 'email', message: 'Invalid email' }]}>
                                    <Input placeholder="vendor@example.com" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="phone" label="Phone" rules={[{ pattern: /^\+?[0-9\-\s]{7,15}$/i, message: 'Invalid phone' }]}>
                                    <Input placeholder="+1234567890" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="city_id" label="City" rules={[{ required: true, message: 'Select city' }]}>
                                    <Select placeholder="Select city">
                                        {cities.map(c => (
                                            <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="min_order_amount" label="Min Order Amount" rules={[{ type: 'number', transform: (v)=> Number(v), min: 0, message: 'Must be >= 0' }]}>
                                    <Input type="number" min={0} placeholder="0" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="is_active" valuePropName="checked" label="Active" initialValue={true}>
                                    <Switch defaultChecked />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item name="address" label="Address" rules={[{ max: 200, message: 'Max 200 chars' }]}>
                            <Input.TextArea rows={2} placeholder="Enter vendor address" />
                        </Form.Item>
                        <Form.Item name="description" label="Description" rules={[{ max: 1000, message: 'Max 1000 chars' }]}>
                            <Input.TextArea rows={4} placeholder="Enter vendor description" />
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </div>
    );
}

export default AdminVendors;
